import { useState } from 'react';
import { PageHeader } from '@/components/composites/PageHeader';
import { SectionCard } from '@/components/composites/SectionCard';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { Select } from '@/components/primitives/Select';
import { Checkbox } from '@/components/primitives/Checkbox';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { noop } from '@/lib/noop';
import styles from './UserSettingsPage.module.css';

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Europe/London', label: 'Europe/London' },
] as const;

const DATE_FORMAT_OPTIONS = [
  { value: 'MMM D, YYYY', label: 'MMM D, YYYY (Jan 15, 2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
] as const;

const HOURS_FORMAT_OPTIONS = [
  { value: 'decimal', label: 'Decimal (6.5h)' },
  { value: 'clock', label: 'Clock (6:30)' },
] as const;

const WEEK_START_OPTIONS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Sunday', label: 'Sunday' },
  { value: 'Saturday', label: 'Saturday' },
] as const;

interface Session {
  name: string;
  detail: string;
  time: string;
}

const SESSIONS: Session[] = [
  {
    name: 'This device',
    detail: 'Chrome on Windows \u00b7 New York, NY',
    time: 'Active now',
  },
  {
    name: 'MacBook Pro',
    detail: 'Safari on macOS \u00b7 New York, NY',
    time: '2 hours ago',
  },
];

export function UserSettingsPage() {
  const { data: currentUser } = useCurrentUser();
  const [dailySummary, setDailySummary] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState(true);
  const [gapAlerts, setGapAlerts] = useState(false);

  return (
    <>
      <PageHeader title="My Account" />

      {/* Profile */}
      <SectionCard title="Profile">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            noop();
          }}
        >
          <div className={styles.twoCol}>
            <TextInput
              label="First Name"
              name="firstName"
              defaultValue={currentUser?.name.split(' ')[0] ?? ''}
            />
            <TextInput
              label="Last Name"
              name="lastName"
              defaultValue={currentUser?.name.split(' ')[1] ?? ''}
            />
          </div>
          <div className={styles.fields}>
            <TextInput
              label="Email"
              name="email"
              type="email"
              defaultValue={currentUser?.email ?? ''}
              disabled
              hint="Managed by your identity provider. Contact your admin to change."
            />
            <Select
              label="Timezone"
              value={currentUser?.timezone ?? ''}
              options={TIMEZONE_OPTIONS}
              onChange={noop}
            />
          </div>
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Notifications */}
      <SectionCard title="Notifications">
        <div className={styles.checkboxList}>
          <Checkbox checked={dailySummary} onChange={setDailySummary} label="Daily summary email" />
          <Checkbox
            checked={weeklySummary}
            onChange={setWeeklySummary}
            label="Weekly report email"
          />
          <Checkbox checked={gapAlerts} onChange={setGapAlerts} label="Gap detection alerts" />
        </div>
        <div className={styles.sectionActions}>
          <Button variant="primary" onClick={noop}>
            Save Changes
          </Button>
        </div>
      </SectionCard>

      {/* Display */}
      <SectionCard title="Display">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            noop();
          }}
        >
          <div className={styles.fields}>
            <Select
              label="Date Format"
              value={currentUser?.dateFormat ?? ''}
              options={DATE_FORMAT_OPTIONS}
              onChange={noop}
            />
            <Select
              label="Hours Format"
              value="decimal"
              options={HOURS_FORMAT_OPTIONS}
              onChange={noop}
            />
            <Select
              label="Week Starts On"
              value="Monday"
              options={WEEK_START_OPTIONS}
              onChange={noop}
            />
          </div>
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Active Sessions */}
      <SectionCard title="Active Sessions">
        <table className={styles.sessionsTable}>
          <tbody>
            {SESSIONS.map((s, i) => (
              <tr key={i}>
                <td className={styles.sessionName}>{s.name}</td>
                <td className={styles.sessionDetail}>{s.detail}</td>
                <td className={styles.sessionTime}>{s.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </>
  );
}
